import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessageWrappedInSpan } from '../../misc';
import { trackEvent } from '../../../utils/ga-tracking';
import classNames from 'classnames';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import CircularProgress from '@material-ui/core/CircularProgress';
import { AppModernization } from '@carbon/pictograms-react';

import environment from '../../../environment';
import AddReminderForEventWhenExpired from '../../../mutations/AddReminderForEventWhenExpiredMutation';
import CustomDialog from '../dialogs/CustomDialog';
import EventReminderForm from './EventReminderForm';
import EventReminderResult from './EventReminderResult';
import SubsectionInFrame from '../../misc/SubsectionInFrame';

const EventReminder = (props) => {
  const {
    onRequestReminder,
    showReminderResult,
    reminderResultSuccessful,
    showNewsletterResult,
    newsletterResultStatus,
    showDialog,
    onScrollToBestOf,
    allowToSubscribeAgain,
    isTokenValid,
    reminderTokenIsInPath,
    reminderToken,
    isUserEmailUnknown,
  } = props;

  const [formVisible, setFormVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(showDialog);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [showResendingResult, setShowResendingResult] = useState(false);
  const [resendReminderSuccessful, setResendReminderSuccessful] = useState(false);

  const openDialog = () => {
    setDialogOpen(true);
    setFormVisible(true);
    trackEvent('Marketing', 'Event Reminder', 'dialog open');
  };

  const closeDialog = () => {
    if (onScrollToBestOf) {
      onScrollToBestOf();
    }
    setDialogOpen(false);
  };

  const closeDialogandAllowToSubmitAgain = () => {
    if (onScrollToBestOf) {
      onScrollToBestOf();
    }
    setDialogOpen(false);
    allowToSubscribeAgain();
  };

  const resendReminder = () => {
    setIsRequestPending(true);
    AddReminderForEventWhenExpired.commit(environment, reminderToken, (err, status) => {
      if (status === 'ok') {
        setResendReminderSuccessful(true);
      }
      setIsRequestPending(false);
      setShowResendingResult(true);
    });
  };

  const eventReminderBody = (
    <>
      <AppModernization className="pictogram-100" />
      <FormattedMessageWrappedInSpan
        id="eventReminder.title"
        defaultMessage="We are on it!"
        className="typography uppercase typography-title_medium mt-10 mb-10"
      />
      <FormattedMessageWrappedInSpan
        id="eventReminder.noPhotosText"
        defaultMessage="The photos are not online yet."
        description="The text that tells the user that the fotos are not online yet."
      />
      <FormattedMessageWrappedInSpan
        id="eventReminder.requestReminderText"
        defaultMessage="Get a notification as soon as your pictures are online."
        description="The text that tells the user that he can get a reminder"
      />
    </>
  );

  return (
    <>
      <SubsectionInFrame actionable center topPadding gap={10} lastInRow>
        {eventReminderBody}
        <button
          className="full-width primary-new subsection-action-button mt-20"
          onClick={openDialog}
        >
          <FormattedMessageWrappedInSpan
            id="eventReminder.action.accept"
            defaultMessage="Notify me"
            className="typography bold-text typography-title_xsmall"
          />
        </button>
      </SubsectionInFrame>

      <CustomDialog open={dialogOpen} onRequestClose={closeDialog}>
        {!showReminderResult && !showResendingResult && formVisible && (
          <EventReminderForm onRequestReminder={onRequestReminder} />
        )}
        {!formVisible && !showResendingResult && (
          <div className="notification-container">
            <DialogContent>
              {!isTokenValid && reminderTokenIsInPath && !isRequestPending && (
                <div className="notification-body">
                  <div className="event-reminder-icon" />
                  <h1>
                    <FormattedMessageWrappedInSpan
                      id="eventReminder.error.title"
                      defaultMessage="Reminder link is invalid or has expired"
                    />
                  </h1>
                  <FormattedMessageWrappedInSpan
                    id="eventReminder.error.text"
                    defaultMessage="Your link is invalid or has expired. Try subscribing for reminder again."
                  />
                </div>
              )}
              {isRequestPending && (
                <div className="notification-body">
                  <CircularProgress />
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <div className="notification-action">
                <button onClick={closeDialog}>
                  <FormattedMessageWrappedInSpan
                    id="eventReminder.cancelButton"
                    defaultMessage="Cancel"
                  />
                </button>
                <button
                  className={classNames('accept', { disabled: isRequestPending })}
                  disabled={isRequestPending}
                  onClick={
                    !isTokenValid && reminderTokenIsInPath && !isUserEmailUnknown ? resendReminder : openDialog
                  }
                >
                  {!isTokenValid && reminderTokenIsInPath ? (
                    <FormattedMessageWrappedInSpan
                      id="eventReminder.acceptAgainButton"
                      defaultMessage="Try again"
                    />
                  ) : (
                    <FormattedMessageWrappedInSpan
                      id="eventReminder.acceptButton"
                      defaultMessage="Notify me"
                    />
                  )}
                </button>
              </div>
            </DialogActions>
          </div>
        )}
        <EventReminderResult
          showReminderResult={showReminderResult || showResendingResult}
          reminderResultSuccessful={reminderResultSuccessful || resendReminderSuccessful}
          showNewsletterResult={showNewsletterResult}
          newsletterResultStatus={newsletterResultStatus}
          closeDialog={closeDialogandAllowToSubmitAgain}
        />
      </CustomDialog>
    </>
  );
};

EventReminder.propTypes = {
  onRequestReminder: PropTypes.func,
  showReminderResult: PropTypes.bool,
  reminderResultSuccessful: PropTypes.bool,
  showNewsletterResult: PropTypes.bool,
  newsletterResultStatus: PropTypes.string,
  showDialog: PropTypes.bool,
  onScrollToBestOf: PropTypes.func,
  allowToSubscribeAgain: PropTypes.func,
  isTokenValid: PropTypes.bool,
  reminderTokenIsInPath: PropTypes.bool,
  reminderToken: PropTypes.string,
  isUserEmailUnknown: PropTypes.bool,
};

export default EventReminder;
